import React, { useState } from 'react'
import {
  Button,
  ContentContainer,
  FormColumn,
  FormGroup,
  FormRow,
  FormWrapper,
  Heading,
  PageContainer,
  SectionContainer,
  Sidebar,
  LoadingScreen
} from '../../ui'

import { useFormik } from 'formik'
import * as yup from 'yup'

import { Profile, TextField, Uploader } from '../../components'
import { useFetch, usePost } from '../../hooks/useXHR'

const ENDPOINT_URL = '/getContact.php'

export const Contact: React.FC = () => {
  const [posting, setPosting] = useState<boolean>(false)
  const { data, loading: fetchingData } = useFetch(ENDPOINT_URL)
  const { action: post } = usePost()

  const initialValues = {
    heading: data?.contact[0]?.heading || '',
    article: data?.contact[0]?.article || '',
    phone: data?.contact[0]?.phone || '',
    whatsapp: data?.contact[0]?.whatsapp || '',
    email: data?.contact[0]?.email || '',
    image: data?.contact[0]?.image || ''
  }

  const validationSchema = yup.object().shape({
    heading: yup
      .string()
      .max(15, 'Título da seção não pode ultrapassar 20 caracteres')
      .required('Esse campo é obrigatório')
      .trim(),
    article: yup.string().required('Esse campo é obrigatório'),
    phone: yup.string().required('Esse campo é obrigatório'),
    whatsapp: yup.string().max(14, 'Número de celular inválido').trim(),
    email: yup
      .string()
      .email('Email inválido')
      .required('Este campo é obrigatório'),
    image: yup.string().required('Esse campo é obrigatório')
  })

  const {
    errors,
    touched,
    values,
    getFieldProps,
    isValid,
    setFieldValue,
    setFieldTouched,
    validateForm,
    handleSubmit
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    isInitialValid: validationSchema.isValidSync(initialValues),
    validateOnBlur: true,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: formValues => {
      setPosting(true)
      const { heading, article, phone, whatsapp, email, image } = formValues
      post(ENDPOINT_URL, {
        heading: heading,
        article: article,
        phone: phone,
        whatsapp: whatsapp,
        email: email,
        image: image,
        id: data?.contact[0]?.id
      }).then(() => {
        window.location.reload(false)
      })
    }
  })

  const handleImageUpload = (file: string) => {
    setFieldValue('image', file)
    setFieldTouched('image', true, false)
    validateForm({ ...values, image: file })
  }

  const canPost = (): boolean => {
    const { heading, article, phone, whatsapp, email, image } = values
    if (heading !== initialValues.heading) return true
    if (article !== initialValues.article) return true
    if (phone !== initialValues.phone) return true
    if (whatsapp !== initialValues.whatsapp) return true
    if (email !== initialValues.email) return true
    if (image !== initialValues.image) return true
    return false
  }

  if (fetchingData) return <LoadingScreen />

  return (
    <PageContainer>
      <Sidebar />
      <SectionContainer>
        <Profile />
        <ContentContainer>
          <FormWrapper alignCenter>
            <form onSubmit={handleSubmit}>
              <FormRow>
                <FormColumn>
                  <Heading>Contato</Heading>
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <TextField
                    label="Título da seção"
                    maxLength={15}
                    {...getFieldProps('heading')}
                    error={
                      errors.heading && touched.heading ? errors.heading : ''
                    }
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <TextField
                    label="Slogan"
                    {...getFieldProps('article')}
                    error={
                      errors.article && touched.article ? errors.article : ''
                    }
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <TextField
                    label="Número(s) de telefone"
                    {...getFieldProps('phone')}
                    error={errors.phone && touched.phone ? errors.phone : ''}
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <TextField
                    label="WhatsApp"
                    {...getFieldProps('whatsapp')}
                    error={
                      errors.whatsapp && touched.whatsapp ? errors.whatsapp : ''
                    }
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <TextField
                    label="Email"
                    {...getFieldProps('email')}
                    error={errors.email && touched.email ? errors.email : ''}
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <FormGroup
                    title="Imagem de fundo"
                    error={errors.image && touched.image ? errors.image : ''}
                  >
                    <Uploader
                      onUpload={handleImageUpload}
                      {...getFieldProps('image')}
                    />
                  </FormGroup>
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn>
                  <Button
                    primary
                    loading={posting}
                    disabled={!isValid || !canPost()}
                  >
                    Salvar alterações
                  </Button>
                </FormColumn>
              </FormRow>
            </form>
          </FormWrapper>
        </ContentContainer>
      </SectionContainer>
    </PageContainer>
  )
}
